/**
 * UserSettingsModal Component
 *
 * Centralized user preferences (Notifications, Profile, Appearance).
 *
 * Decomposed structure:
 * - utils/settingsLogic.ts   — pure settings migration + state transitions (tested)
 * - utils/browserInfo.ts     — pure browser/platform detection helpers (tested)
 * - hooks/useNotificationSettings.ts — settings state, Firestore load/save
 * - hooks/useDeviceManagement.ts     — FCM device toggle/remove flow
 * - components/              — tab nav + per-tab section components
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BaseModal, useFirebaseStore, useToast, useLocations, type NotificationPageId } from '@/shared';
import { doc, setDoc } from 'firebase/firestore';
import { logger } from '../../utils/logger';
import { useFCMToken, type FCMTokenEntry } from '../../hooks/useFCMToken';
import { usePWAInstall } from '../../hooks/usePWAInstall';
import { detectBrowserInfo, getBrowserInstructions } from './utils/browserInfo';
import { useNotificationSettings } from './hooks/useNotificationSettings';
import { useDeviceManagement } from './hooks/useDeviceManagement';
import { SettingsTabNav, type SettingsTab } from './components/SettingsTabNav';
import { NotificationsTab } from './components/NotificationsTab';
import { ProfileTab } from './components/ProfileTab';
import { AppearanceTab } from './components/AppearanceTab';
import { useThemeStore } from '../../store/themeStore';
import type { ThemePreference } from '../../utils/themeBoot';

interface UserSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const UserSettingsModal: React.FC<UserSettingsModalProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<SettingsTab>('notifications');
  const { currentUser, db } = useFirebaseStore();
  const { showToast } = useToast();
  const locations = useLocations();
  const setThemePreference = useThemeStore((state) => state.setPreference);
  const {
    permissionStatus,
    requestBrowserPermission,
    registerFCMToken,
    isRequestingPermission,
    refreshPermissionStatus,
    updateDeviceEnabled,
    removeDevice,
    currentDeviceId,
  } = useFCMToken();

  const {
    canInstall,
    isInstalled,
    isPrompting,
    promptInstall,
    debugInfo,
  } = usePWAInstall();

  // Notification settings state + Firestore load/save + toggle actions
  const settingsController = useNotificationSettings();
  const { loadSettings, saveSettings } = settingsController;

  // Device management state (toggle debounce + remove confirmation)
  const {
    deviceToRemove,
    setDeviceToRemove,
    isRemovingDevice,
    pendingToggleDeviceId,
    handleDeviceToggle,
    handleDeviceRemove,
  } = useDeviceManagement({ updateDeviceEnabled, removeDevice, showToast });

  // Progressive disclosure state
  const [expandedLocations, setExpandedLocations] = useState<Set<NotificationPageId>>(new Set());
  const [expandedAdvanced, setExpandedAdvanced] = useState<Set<NotificationPageId>>(new Set());
  const [devicesExpanded, setDevicesExpanded] = useState(false);

  // Appearance settings
  const [headerPinned, setHeaderPinned] = useState(
    currentUser?.uiPreferences?.headerPinned ?? false
  );

  // Detect browser and platform for specific notification permission messaging
  const browserInfo = detectBrowserInfo();
  const browserInstructions = getBrowserInstructions(browserInfo);

  // Check if browser permission is granted
  const hasBrowserPermission = permissionStatus === 'granted';

  useEffect(() => {
    if (isOpen && currentUser && db) {
      loadSettings();
      // Refresh browser permission status when modal opens
      refreshPermissionStatus();
      // Sync header pinned state from current user
      setHeaderPinned(currentUser?.uiPreferences?.headerPinned ?? false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentUser, db, refreshPermissionStatus]);

  const handleSave = async () => {
    const success = await saveSettings();
    if (success) {
      onClose();
    }
  };

  /**
   * Handle requesting browser notification permission (Step 1).
   * After permission is granted, automatically registers FCM token.
   */
  const handleRequestBrowserPermission = useCallback(async () => {
    const granted = await requestBrowserPermission();

    if (granted) {
      showToast('Browser notifications enabled!', 'success');
      // Automatically register FCM token in background
      registerFCMToken().then((success) => {
        if (!success) {
          // Token registration failed, but permission was granted
          // User can still use notifications, token will be retried later
          logger.warn('[UserSettings] FCM token registration failed, but permission was granted');
        }
      });
    } else {
      // Check actual browser permission to give appropriate feedback
      const actualPermission = typeof Notification !== 'undefined' ? Notification.permission : 'denied';
      if (actualPermission === 'default') {
        showToast(
          browserInfo.isEdge
            ? 'Permission request timed out. Check Windows notification settings for Edge.'
            : 'Permission request timed out. Check your system notification settings.',
          'warning'
        );
      } else if (actualPermission === 'denied') {
        showToast('Notifications were blocked. See instructions above to enable.', 'warning');
      }
    }
  }, [requestBrowserPermission, registerFCMToken, showToast, browserInfo.isEdge]);

  /**
   * Handle toggling the header pinned preference
   * Saves immediately to Firestore using merge
   */
  const handleHeaderPinnedChange = useCallback(async (pinned: boolean) => {
    setHeaderPinned(pinned);
    if (currentUser?.email && db) {
      try {
        const userRef = doc(db, 'users', currentUser.email);
        logger.firestore('Write', `users/${currentUser.email} (save header pinned)`);
        await setDoc(userRef, {
          uiPreferences: { headerPinned: pinned }
        }, { merge: true });
      } catch (error) {
        logger.error('Failed to save header pinned preference:', error);
        showToast('Failed to save preference', 'error');
        // Revert on error
        setHeaderPinned(!pinned);
      }
    }
  }, [currentUser?.email, db, showToast]);

  /**
   * Handle changing the theme preference.
   *
   * Applies locally first and unconditionally: localStorage is the paint-time
   * source of truth, so the theme must switch even for a signed-out user or a
   * failed write. The Firestore copy is cross-device sync only, which is why a
   * failure here warns but does not revert — reverting would undo a change the
   * user can plainly see worked.
   */
  const handleThemeChange = useCallback(async (theme: ThemePreference) => {
    setThemePreference(theme);
    if (currentUser?.email && db) {
      try {
        const userRef = doc(db, 'users', currentUser.email);
        logger.firestore('Write', `users/${currentUser.email} (save theme)`);
        await setDoc(userRef, { uiPreferences: { theme } }, { merge: true });
      } catch (error) {
        logger.error('Failed to save theme preference:', error);
        showToast('Theme applied here, but could not sync to your other devices', 'warning');
      }
    }
  }, [currentUser?.email, db, setThemePreference, showToast]);

  /**
   * Toggle locations section expanded state
   */
  const toggleLocationsExpanded = useCallback((pageId: NotificationPageId) => {
    setExpandedLocations((prev) => {
      const next = new Set(prev);
      if (next.has(pageId)) {
        next.delete(pageId);
      } else {
        next.add(pageId);
      }
      return next;
    });
  }, []);

  /**
   * Toggle advanced settings expanded state (for trucking calendar)
   */
  const toggleAdvancedExpanded = useCallback((pageId: NotificationPageId) => {
    setExpandedAdvanced((prev) => {
      const next = new Set(prev);
      if (next.has(pageId)) {
        next.delete(pageId);
      } else {
        next.add(pageId);
      }
      return next;
    });
  }, []);

  /**
   * Get FCM tokens from current user
   */
  const fcmTokens: FCMTokenEntry[] = useMemo(() => {
    if (!currentUser?.fcmTokens) return [];
    // Filter and cast to FCMTokenEntry, handling legacy formats
    return (currentUser.fcmTokens as FCMTokenEntry[]).filter((t) => t && typeof t === 'object');
  }, [currentUser?.fcmTokens]);

  /**
   * Handle the PWA install prompt (Android)
   */
  const handleInstall = useCallback(async () => {
    const accepted = await promptInstall();
    if (accepted) {
      showToast('App installed successfully!', 'success');
    }
  }, [promptInstall, showToast]);

  if (!currentUser) return null;

  const isAndroid = /Android/.test(navigator.userAgent);

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="User Settings"
      icon="fa-solid fa-user-gear text-ink-body"
      maxWidth="4xl"
      footer={
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="btn btn-secondary" disabled={settingsController.isSaving}>
            Cancel
          </button>
          {/* Disabled until notification settings change. Appearance options save
              on change, so they never need this button. */}
          <button
            onClick={handleSave}
            className="btn btn-primary"
            disabled={settingsController.isSaving || settingsController.isLoading || !settingsController.isDirty}
            title={settingsController.isDirty ? undefined : 'No changes to save'}
          >
            {settingsController.isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      }
    >
      <div className="flex flex-col sm:flex-row sm:h-[60vh] sm:min-h-[400px]">
        {/* Tab Navigation - horizontal on mobile, vertical sidebar on desktop */}
        <SettingsTabNav activeTab={activeTab} onTabChange={setActiveTab} />

        {/* Content */}
        <div className="flex-1 sm:pl-6 overflow-y-auto max-h-[60vh] sm:max-h-none">
          {settingsController.isLoading ? (
            <div className="flex justify-center items-center h-full text-ink-faint">
              <i className="fa-solid fa-circle-notch fa-spin text-2xl"></i>
            </div>
          ) : (
            <>
              {activeTab === 'notifications' && (
                <NotificationsTab
                  controller={settingsController}
                  locations={locations}
                  browserInfo={browserInfo}
                  browserInstructions={browserInstructions}
                  permission={{
                    status: permissionStatus,
                    hasBrowserPermission,
                    isRequesting: isRequestingPermission,
                    onRequest: handleRequestBrowserPermission,
                  }}
                  devices={{
                    fcmTokens,
                    currentDeviceId,
                    expanded: devicesExpanded,
                    onToggleExpanded: () => setDevicesExpanded(!devicesExpanded),
                    pendingToggleDeviceId,
                    onToggle: handleDeviceToggle,
                    onRequestRemove: setDeviceToRemove,
                    toRemove: deviceToRemove,
                    isRemoving: isRemovingDevice,
                    onCancelRemove: () => setDeviceToRemove(null),
                    onConfirmRemove: handleDeviceRemove,
                  }}
                  install={{
                    isInstalled,
                    isAndroid,
                    canInstall,
                    isPrompting,
                    onInstall: handleInstall,
                    debugInfo,
                  }}
                  expandedLocations={expandedLocations}
                  expandedAdvanced={expandedAdvanced}
                  onToggleLocationsExpanded={toggleLocationsExpanded}
                  onToggleAdvancedExpanded={toggleAdvancedExpanded}
                />
              )}

              {activeTab === 'profile' && (
                <ProfileTab
                  displayName={currentUser.displayName}
                  email={currentUser.email}
                  role={currentUser.role}
                  defaultLocation={currentUser.defaultLocation}
                  locations={locations}
                />
              )}

              {activeTab === 'appearance' && (
                <AppearanceTab
                  headerPinned={headerPinned}
                  onHeaderPinnedChange={handleHeaderPinnedChange}
                  onThemeChange={handleThemeChange}
                />
              )}
            </>
          )}
        </div>
      </div>
    </BaseModal>
  );
};
